import React, { useState, useEffect, useCallback } from 'react';
import { PageHeader, StatCards, Card, TabBar, Field, inputStyle, btnPrimary, btnSm, Empty, fmtKES , useBreakpoint } from '../components/UI';
import { printKidsReceipt } from '../utils/print';
import * as api from '../api/client';
import ComplimentaryModal from '../components/ComplimentaryModal';

const KIDS_ACTIVITIES = [
  { id:'water_slide',   label:'Water Slide',    price:200, icon:'🌊', duration:'Per session',  minAge:3  },
  { id:'trampoline',    label:'Trampoline',     price:300, icon:'⬆️', duration:'Per 30 mins',  minAge:4  },
  { id:'mini_golf',     label:'Mini Golf',      price:400, icon:'⛳', duration:'Per round',    minAge:5  },
  { id:'bouncy_castle', label:'Bouncy Castle',  price:200, icon:'🏰', duration:'Per 30 mins',  minAge:2  },
  { id:'face_paint',    label:'Face Painting',  price:250, icon:'🎨', duration:'Per child',    minAge:2  },
  { id:'arts_crafts',   label:'Arts & Crafts',  price:350, icon:'✂️', duration:'Materials incl.',minAge:3 },
  { id:'story_time',    label:'Story Time',     price:150, icon:'📚', duration:'Per session',  minAge:2  },
  { id:'puppet_show',   label:'Puppet Show',    price:0,   icon:'🎭', duration:'Scheduled',    minAge:1  },
  { id:'board_games',   label:'Board Games',    price:100, icon:'🎲', duration:'Per hour',     minAge:5  },
  { id:'playground',    label:'Playground',     price:0,   icon:'🛝', duration:'Open access',  minAge:1  },
];

const PAY_MODES = [
  { value:'cash',          label:'Cash',          color:'#2ecc71' },
  { value:'mpesa',         label:'M-Pesa',        color:'#00b300' },
  { value:'card',          label:'Card / PDQ',    color:'#9b59b6' },
  { value:'room_charge',   label:'Room Charge',   color:'#e67e22' },
  { value:'complimentary', label:'Complimentary', color:'#c8a96e' },
];

export default function KidsAmenitiesPage({ notify }) {
  const bp = useBreakpoint();
  const [tab, setTab]         = useState('booking');
  const [bookings, setBookings] = useState([]);
  const [guests, setGuests]   = useState([]);
  const [cart, setCart]       = useState([]);
  const [busy, setBusy]       = useState(false);
  const [showComp, setShowComp] = useState(false);

  const blank = { child_name:'', child_age:'', parent_name:'', payment_mode:'cash', tx_code:'', guest_id:'' };
  const [form, setForm] = useState(blank);

  const load = useCallback(async () => {
    try {
      const [g, k] = await Promise.all([api.getGuests(), api.getKidsAmenities()]);
      setGuests((g.data.results || g.data).filter(x => x.status === 'checked-in'));
      setBookings(k.data.results || k.data || []);
    } catch {}
  }, []);

  useEffect(() => { load(); }, [load]);

  const addToCart = (activity) => {
    setCart(p => {
      const existing = p.find(x => x.id === activity.id);
      if (existing) return p.map(x => x.id === activity.id ? {...x, qty: x.qty + 1} : x);
      return [...p, {...activity, qty:1}];
    });
  };

  const removeFromCart = (id) => setCart(p => p.filter(x => x.id !== id));

  const cartTotal = cart.reduce((s, x) => s + x.price * x.qty, 0);

  const submit = async () => {
    if (!form.child_name) { notify('Child name required', 'error'); return; }
    if (cart.length === 0) { notify('Add at least one activity', 'error'); return; }
    if (form.payment_mode === 'complimentary') { setShowComp(true); return; }
    if (cartTotal > 0 && form.payment_mode !== 'room_charge' && !form.tx_code) { notify('Transaction reference required', 'error'); return; }
    setBusy(true);
    try {
      const kr = await api.createKidsBooking({
        child_name: form.child_name,
        child_age: form.child_age,
        parent_name: form.parent_name,
        payment_mode: form.payment_mode,
        tx_code: form.tx_code,
        guest_id: form.guest_id || undefined,
        amount: cartTotal,
        activities: cart.map(x => `${x.label} x${x.qty}`).join(', '),
      });
      notify(`Kids booking recorded — ${fmtKES(cartTotal)} ✅`);
      const receiptData = { ...(kr?.data || {}), child_name: form.child_name, child_age: form.child_age, parent_name: form.parent_name, payment_mode: form.payment_mode, tx_code: form.tx_code, amount: cartTotal, items: cart.map(x => ({ name: x.label, amenity_name: x.label, qty: x.qty || 1, price: x.price })) };
      printKidsReceipt(receiptData);
      setForm(blank);
      setCart([]);
      load();
    } catch(e) { notify(e.response?.data?.detail || 'Error recording booking', 'error'); }
    finally { setBusy(false); }
  };

  const todayBookings = bookings.filter(b => {
    const d = new Date(b.paid_at || b.created_at);
    return d.toDateString() === new Date().toDateString();
  });
  const todayRevenue = todayBookings.reduce((s, b) => s + parseFloat(b.amount || 0), 0);

  return (
    <div>
      <PageHeader icon="🧒" title="Kids Amenities" sub="Manage kids activities, bookings & entertainment" />

      <StatCards stats={[
        { label:"Today's Bookings", value: todayBookings.length, color:'#f472b6', icon:'🧒' },
        { label:"Today's Revenue", value: fmtKES(todayRevenue), color:'#2ecc71', icon:'💰' },
        { label:'Activities Available', value: KIDS_ACTIVITIES.length, color:'#8b5cf6', icon:'🎮' },
        { label:'Cart Total', value: fmtKES(cartTotal), color:'#f59e0b', icon:'🛒' },
      ]} />

      <TabBar
        tabs={[
          { id:'booking', label:'New Booking', icon:'➕' },
          { id:'activities', label:'Activities Menu', icon:'🎮' },
          { id:'history', label:'Bookings Log', icon:'📋' },
          { id:'schedule', label:"Today's Schedule", icon:'📅' },
        ]}
        active={tab}
        onChange={setTab}
      />

      {tab === 'activities' && (
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(200px,1fr))', gap:12 }}>
          {KIDS_ACTIVITIES.map(a => (
            <div key={a.id} style={{ background:'var(--bgCard)', border:'1px solid var(--border)', borderRadius:8, padding:14, cursor:'pointer', transition:'all .2s' }}
              onClick={() => addToCart(a)}>
              <div style={{ fontSize:32, marginBottom:8 }}>{a.icon}</div>
              <div style={{ fontWeight:700, color:'var(--textPrimary)', fontSize:14, marginBottom:4 }}>{a.label}</div>
              <div style={{ fontSize:11, color:'var(--textMuted)', marginBottom:8 }}>{a.duration} · Min age: {a.minAge}+</div>
              <div style={{ fontWeight:800, color: a.price > 0 ? '#2ecc71' : '#3498db', fontSize:16 }}>
                {a.price > 0 ? fmtKES(a.price) : 'FREE'}
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === 'booking' && (
        <div style={{ display:'grid', gridTemplateColumns: bp === 'sm' ? '1fr' : '1fr 380px', gap:16, alignItems:'start' }}>
          <div>
            <Card title="Select Activities">
              <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(180px,1fr))', gap:10 }}>
                {KIDS_ACTIVITIES.map(a => {
                  const inCart = cart.find(x => x.id === a.id);
                  return (
                    <div key={a.id}
                      onClick={() => addToCart(a)}
                      style={{ padding:'10px 12px', borderRadius:6, border:`2px solid ${inCart ? '#f472b6' : 'var(--border)'}`, background: inCart ? 'var(--bgSuccess)' : 'var(--bgCard)', cursor:'pointer', transition:'all .15s', position:'relative' }}>
                      <div style={{ fontSize:22 }}>{a.icon}</div>
                      <div style={{ fontWeight:600, color:'var(--textPrimary)', fontSize:12, marginTop:4 }}>{a.label}</div>
                      <div style={{ fontSize:11, color:'var(--textMuted)' }}>{a.duration}</div>
                      <div style={{ fontWeight:700, color: a.price > 0 ? '#2ecc71' : '#3498db', fontSize:13, marginTop:4 }}>
                        {a.price > 0 ? fmtKES(a.price) : 'FREE'}
                      </div>
                      {inCart && <div style={{ position:'absolute', top:6, right:8, background:'#f472b6', color:'#fff', borderRadius:'50%', width:18, height:18, display:'flex', alignItems:'center', justifyContent:'center', fontSize:10, fontWeight:700 }}>{inCart.qty}</div>}
                    </div>
                  );
                })}
              </div>
            </Card>

            <Card title="Child Details" style={{ marginTop:0 }}>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(220px,1fr))', gap:12 }}>
                <Field label="Child's Name">
                  <input style={inputStyle} value={form.child_name} onChange={e => setForm(p => ({...p, child_name:e.target.value}))} placeholder="Child's name" />
                </Field>
                <Field label="Age">
                  <input type="number" style={inputStyle} min={1} max={17} value={form.child_age} onChange={e => setForm(p => ({...p, child_age:e.target.value}))} placeholder="Age" />
                </Field>
                <Field label="Parent / Guardian Name">
                  <input style={inputStyle} value={form.parent_name} onChange={e => setForm(p => ({...p, parent_name:e.target.value}))} placeholder="Parent's name" />
                </Field>
                <Field label="Hotel Guest (optional)">
                  <select style={inputStyle} value={form.guest_id} onChange={e => setForm(p => ({...p, guest_id:e.target.value}))}>
                    <option value="">— Walk-in / Non-resident —</option>
                    {guests.map(g => <option key={g.id} value={g.id}>{g.guest_name} — Rm {g.room_number}</option>)}
                  </select>
                </Field>
              </div>
            </Card>
          </div>

          <Card title="Booking Summary">
            {cart.length === 0 ? (
              <Empty text="Add activities to the cart" />
            ) : (
              <>
                <div style={{ display:'grid', gap:8, marginBottom:12 }}>
                  {cart.map(item => (
                    <div key={item.id} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'8px 10px', background:'var(--bgInput)', borderRadius:6 }}>
                      <div>
                        <div style={{ fontWeight:600, color:'var(--textPrimary)', fontSize:13 }}>{item.icon} {item.label}</div>
                        <div style={{ fontSize:11, color:'var(--textMuted)' }}>Qty: {item.qty} × {fmtKES(item.price)}</div>
                      </div>
                      <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                        <span style={{ fontWeight:700, color:'#2ecc71' }}>{fmtKES(item.price * item.qty)}</span>
                        <button style={{ background:'#e74c3c', border:'none', borderRadius:3, color:'#fff', width:20, height:20, cursor:'pointer', fontSize:12, lineHeight:1 }}
                          onClick={() => removeFromCart(item.id)}>×</button>
                      </div>
                    </div>
                  ))}
                </div>

                <div style={{ borderTop:'2px solid var(--border)', paddingTop:12, marginBottom:14 }}>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                    <span style={{ fontWeight:700, color:'var(--textSecond)' }}>Total</span>
                    <span style={{ fontSize:22, fontWeight:800, color: cartTotal > 0 ? '#2ecc71' : '#3498db' }}>
                      {cartTotal > 0 ? fmtKES(cartTotal) : 'FREE'}
                    </span>
                  </div>
                </div>

                {cartTotal > 0 && (
                  <>
                    <Field label="Payment Mode">
                      <select style={inputStyle} value={form.payment_mode} onChange={e => setForm(p => ({...p, payment_mode:e.target.value}))}>
                        {PAY_MODES.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                      </select>
                    </Field>
                    {form.payment_mode !== 'room_charge' && (
                      <Field label="Transaction Reference">
                        <input style={inputStyle} value={form.tx_code} onChange={e => setForm(p => ({...p, tx_code:e.target.value}))} placeholder="M-Pesa code / receipt no." />
                      </Field>
                    )}
                  </>
                )}

                <button style={{...btnPrimary, background:'#f472b6', marginTop:8}} onClick={submit} disabled={busy}>
                  {busy ? '⏳ Recording…' : '✅ Confirm Booking'}
                </button>
              </>
            )}
          </Card>
        </div>
      )}

      {tab === 'history' && (
        <Card title="Bookings Log">
          {bookings.length === 0 ? <Empty text="No bookings yet" /> : (
            <div style={{ overflowX:'auto' }}>
              <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
                <thead>
                  <tr style={{ borderBottom:'2px solid var(--border)' }}>
                    {['Date','Child','Parent','Activities','Amount','Mode','Receipt'].map(h => (
                      <th key={h} style={{ padding:'8px 10px', textAlign:'left', fontSize:10, color:'var(--textMuted)', fontWeight:700, textTransform:'uppercase', letterSpacing:0.5 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {[...bookings].reverse().map((b, i) => (
                    <tr key={b.id || i} style={{ borderBottom:'1px solid var(--border)', background: i%2===0?'transparent':'rgba(0,0,0,0.02)' }}>
                      <td style={{ padding:'8px 10px', color:'var(--textMuted)', fontSize:11 }}>{(b.paid_at||b.created_at||'').slice(0,10)}</td>
                      <td style={{ padding:'8px 10px', fontWeight:700, color:'var(--textPrimary)' }}>{b.child_name}</td>
                      <td style={{ padding:'8px 10px', color:'var(--textSecond)' }}>{b.parent_name||'—'}</td>
                      <td style={{ padding:'8px 10px', color:'var(--textMuted)', fontSize:11, maxWidth:160, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{b.activities||'—'}</td>
                      <td style={{ padding:'8px 10px', fontWeight:700, color:'#2ecc71' }}>{fmtKES(b.amount)}</td>
                      <td style={{ padding:'8px 10px' }}>
                        <span style={{ fontSize:10, fontWeight:700, background:'rgba(200,169,110,0.15)', color:'#c8a96e', padding:'2px 7px', borderRadius:3, textTransform:'capitalize' }}>{(b.payment_mode||'—').replace('_',' ')}</span>
                      </td>
                      <td style={{ padding:'8px 10px' }}>
                        <button style={{ ...btnSm, fontSize:10, padding:'3px 9px' }} onClick={() => printKidsReceipt(b)}>🖨 Print</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {tab === 'schedule' && (
        <Card title="Today's Activities Schedule">
          <p style={{ color:'var(--textMuted)', fontSize:13, marginBottom:12 }}>Scheduled group activities for today.</p>
          <div style={{ display:'grid', gap:10 }}>
            {[
              { time:'09:00', activity:'Morning Splash Time', guide:'Pool Attendant', capacity:15 },
              { time:'10:30', activity:'Arts & Crafts Session', guide:'Kids Staff', capacity:10 },
              { time:'11:30', activity:'Story Time', guide:'Kids Staff', capacity:20 },
              { time:'14:00', activity:'Puppet Show', guide:'Entertainment', capacity:30 },
              { time:'15:30', activity:'Mini Olympics', guide:'Sports Staff', capacity:20 },
              { time:'17:00', activity:'Evening Pool Party', guide:'Pool Attendant', capacity:25 },
            ].map((s, i) => (
              <div key={i} style={{ display:'flex', alignItems:'center', gap:14, padding:'10px 14px', background:'var(--bgInput)', borderRadius:6, border:'1px solid var(--border)' }}>
                <div style={{ minWidth:52, fontWeight:700, color:'#f472b6', fontSize:15 }}>{s.time}</div>
                <div style={{ flex:1 }}>
                  <div style={{ fontWeight:700, color:'var(--textPrimary)' }}>{s.activity}</div>
                  <div style={{ fontSize:11, color:'var(--textMuted)', marginTop:2 }}>Guide: {s.guide} · Max {s.capacity} children</div>
                </div>
                <span style={{ padding:'3px 10px', borderRadius:3, background:'#8b5cf6', color:'#fff', fontSize:10, fontWeight:700 }}>SCHEDULED</span>
              </div>
            ))}
          </div>
        </Card>
      )}
      {showComp && (
        <ComplimentaryModal
          amount={cartTotal}
          guestName={form.parent_name || form.child_name}
          notify={notify}
          onClose={() => setShowComp(false)}
          onSubmitted={() => { setForm(blank); setCart([]); load(); }}
        />
      )}
    </div>
  );
}
